<template>
	<Form ref="formRef" @submit="handleNextStep" @invalid-submit="onInvalidSubmit">
	<DoorsaleLayout
		:title="$t('checkout.addons')"
		:current-step="1"
		:event-id="eventId"
		:on-back="handleBack"
		:on-forward="handleForward"
	>
		<template #header>
			<CheckoutTicketHeader v-if="checkoutStore.event" />
		</template>

		<div class="space-y-4">
			<CheckoutUpsaleTicket
				v-for="addon in addonAvailableToBasket"
				:key="addon.id"
				:ticket="addon"
				:eventID="eventId"
				:event="checkoutStore.event"
			/>
		</div>

		<template #aside>
			<div class="md:sticky md:top-24">
				<CheckoutSummary :primary-label="$t('doorsale.continue')" />
			</div>
		</template>
	</DoorsaleLayout>
	</Form>
</template>

<script setup lang="ts">
	import { useHead } from '@unhead/vue';
	import { Form, type InvalidSubmissionHandler } from 'vee-validate';
	import { computed, onMounted, ref, watch } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { useRoute, useRouter } from 'vue-router';

	import CheckoutSummary from '@/components/checkout/CheckoutSummary.vue';
	import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
	import CheckoutUpsaleTicket from '@/components/checkout/CheckoutUpsaleTicket.vue';
	import DoorsaleLayout from '@/components/doorsale/DoorsaleLayout.vue';
	import { useCartStore } from '@/stores/cart';
	import { useCheckoutStore } from '@/stores/checkoutStore';
	import { useToastStore } from '@/stores/toastStore';
	import type { TicketCheckoutData } from '@/types';
	import { useDoorsaleNavigation } from '@/utils/doorsaleNavigation';
	import { useWebsocketStore } from '@/websocket/websocket';

	const { t } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const checkoutStore = useCheckoutStore();
	const cart = useCartStore();
	const toastStore = useToastStore();
	const websocketStore = useWebsocketStore();

	const formRef = ref<InstanceType<typeof Form> | null>(null);

	const eventId = computed<number | string>(() => {
		const p = route.params.eventId as string;
		return /^[0-9a-f]{96}$/.test(p) ? p : Number(p);
	});
	const { nextStep, previousStep } = useDoorsaleNavigation(eventId);

	function handleBack() {
		previousStep('doorsale-upsale');
	}

	function handleForward() {
		(formRef.value?.$el as HTMLFormElement | undefined)?.requestSubmit();
	}

	const addonAvailableToBasket = computed<TicketCheckoutData[]>(() => {
		const upsaleTicketsAvailableToBasket = cart.getAvailableUpsaleTicketsForBasket();

		return checkoutStore.upsaleTickets.filter((upsale_ticket) => {
			if (!upsale_ticket.upsale_flag) return false;

			return upsaleTicketsAvailableToBasket.includes(Number(upsale_ticket.id));
		});
	});

	function handleNextStep() {
		nextStep('doorsale-upsale');
	}

	const onInvalidSubmit: InvalidSubmissionHandler = submission => {
		const firstErrorFieldName = Object.keys(submission.errors)[0];
		const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
		if (el) {
			(el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};

	watch(() => cart.differentCount, newValue => {
		if (newValue) {
			const message = cart.differentCountMessage.message;
			const count = cart.differentCountMessage.count;
			const ticketsText = (count == 1 ? t('common.ticket') : t('common.ticket_plural')).toLocaleLowerCase();
			if (message && message == 'initial') {
				toastStore.showToast(t('checkout.reservation_mismatch', { tickets: ticketsText }), 15000);
			} else if (message && count) {
				toastStore.showToast(t('checkout.reservation_mismatch_' + message, { count, tickets: ticketsText }), 15000);
			}
			cart.differentCount = false;
		}
	});

	onMounted(() => {
		if (!eventId.value) {
			router.push({ name: 'doorsale-events' });
			return;
		}
		checkoutStore.setCheckoutID(eventId.value);
		websocketStore.send('subscribeevent', { id: eventId.value, is_doorsale: true }, undefined);
	});

	useHead({
		title: t('checkout.addons'),
		meta: [{ property: 'og:title', content: t('checkout.addons') }],
	});
</script>
